import React, { useRef, useCallback, useEffect, useState} from 'react';
import { API_URL } from '../config/api';
import './styles/OrderReceipt.css';
import { formatCurrency } from '../utils/dateUtils';
import { normalizeOrderType } from '../utils/orderTypeUtils';
import EmailInvoice from './EmailInvoice';

const OrderReceipt = ({ 
  orderDetails, 
  customerInfo: initialCustomerInfo, 
  wigDetails, 
  orderItems = [], 
  payments = [], 
  totals = {},
  paymentSummary = {},
  isVisible = false,
  onClose,
  lineItemsData = null,
  paymentsData = null,
   skipPrint = false 
}) => {
  const receiptRef = useRef();
  const [customerInfo, setCustomerInfo] = useState(initialCustomerInfo || null);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


    const customerId = orderDetails?.customerNo || orderDetails?.OrderCustomerNo;

useEffect(() => {
    const fetchCustomerInfo = async () => {
      if (!customerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/customers/${customerId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch customer data');
        }
        
        const customerData = await response.json();
        setCustomerInfo(customerData);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerInfo();
  }, [customerId]);

  // Fetch shipping info when orderDetails changes
useEffect(() => {
  const fetchShippingInfo = async () => {
    const customerId = orderDetails?.customerNo;
    if (customerId) {
      try {
        setIsLoadingShipping(true);
        const response = await fetch(`${API_URL}/api/customers/${customerId}/shipping`);
        const data = await response.json();
        // console.log('🚚 Shipping API response:', data); // Add this
        // console.log('🚚 Setting shippingInfo to:', data.shippingAddress); // Add this
        setShippingInfo(data.shippingAddress);
      } catch (err) {
        console.error('Error fetching shipping info:', err);
        setShippingInfo(null);
      } finally {
        setIsLoadingShipping(false);
      }
    }
  };

  fetchShippingInfo();
}, [orderDetails?.orderCustomerNo]);

useEffect(() => {
  if (isVisible && !isLoadingShipping && !skipPrint) {  // ADD !skipPrint
    setTimeout(() => {
      handlePrint();
    }, 100);
  }
}, [isVisible, isLoadingShipping, skipPrint]);


  // // Format currency consistently
  // const formatCurrency = (amount) => {
  //   const num = parseFloat(amount || 0);
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2
  //   }).format(num);
  // };

  
  // Format date consistently
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get company info based on warehouse/location
  const getCompanyInfo = () => {
    // Try multiple possible field names for warehouse
    const warehouse = orderDetails?.orderWarehouse || 
                    orderDetails?.warehouse || 
                    orderDetails?.location || 
                    'Pomona';
    
    console.log('🏢 Warehouse value used:', warehouse);
    console.log('🏢 Full orderDetails:', orderDetails);
    
    switch (warehouse.toLowerCase()) {
      case 'lakewood':
        return {
          name: 'Dini Wigs',
          address: '1876 Lakewood Road',
          cityState: 'Toms River, NJ 08755',
          phone: '732.575.9941',
          website: 'WWW.DiniWigs.com'
        };
      case 'israel':
        return {
          name: 'Dini Wigs Israel',
          address: 'Hartom St 9',
          cityState: 'Jerusalem, Israel',
          phone: '+972-2-123-4567',
          website: 'WWW.DiniWigs.com'
        };
      default: // Pomona
        return {
          name: 'Dini Wigs',
          address: '18 Thiells Mount Ivy Road',
          cityState: 'Pomona, NY 10970',
          phone: '(845) 262-1010',
          website: 'WWW.DiniWigs.com'
        };
    }
  };

  const companyInfo = getCompanyInfo();

  // Calculate totals with tax payment detection
  const calculateTotals = () => {
    const itemsToUse = lineItemsData?.items || orderItems || [];
    const paymentsToUse = paymentsData?.payments || payments || [];

    const totalFees = paymentsToUse.reduce((sum, payment) => {
    const ccFee = parseFloat(payment.CreditCardFee || payment.cardFee || payment.creditCardFee || 0);
    return sum + ccFee;
  }, 0);
    
    // Calculate subtotal from line items (excluding tax items)
    const subtotal = itemsToUse.reduce((sum, item) => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      if (description.includes('tax') || description.includes('processing fee') || description.includes('credit card fee')) {
        return sum;
      }
      const itemTotal = parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
      return sum + itemTotal;
    }, 0);
    
    // Find tax amount from line items
    const taxLineItems = itemsToUse.filter(item => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      return description.includes('tax applied') && !description.includes('credit card') && !description.includes('processing');
    });
      
    const taxFromLineItems = taxLineItems.reduce((sum, item) => {
      return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
    }, 0);
    
    // Analyze payments
    let regularPayments = 0;
    let taxPayments = 0;
    let creditCardFees = 0;
    
    paymentsToUse.forEach(payment => {
      const paymentType = (payment.PaymentType || '').toLowerCase();
      const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);
      const ccFee = parseFloat(payment.CreditCardFee || payment.cardFee || payment.creditCardFee || 0);
      
      if (paymentType.includes('tax')) {
        taxPayments += paymentAmount;
      } else {
        regularPayments += paymentAmount;
      }
      
      creditCardFees += ccFee;
    });
    
    const totalPayments = regularPayments + taxPayments;
    const shippingAmount = parseFloat(totals.shipping || 0);
    const depositAmount = parseFloat(totals.deposit || 0);
    const discountAmount = parseFloat(totals.discount || 0);
    const totalDue = subtotal + taxFromLineItems + shippingAmount + depositAmount - discountAmount;
    const balanceDue = Math.max(0, totalDue - totalPayments);

    return {
      subtotal,
      taxAmount: taxFromLineItems,
      taxPayments,
      shippingAmount,
      depositAmount,
      discountAmount,
      totalPayments,
      creditCardFees,
      totalDue,
      balanceDue,
      totalFees
    };
  };

  const calculatedTotals = calculateTotals();

  const handlePrint = useCallback(() => {
    const printContent = receiptRef.current;
    
    if (!printContent) {
      console.error('Print content not found');
      return;
    }
    
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      
      if (!printWindow) {
        console.error('Failed to open print window - popup might be blocked');
        alert('Please allow popups for printing');
        return;
      }
      const printStyles = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700&display=swap');
          
          @page { 
            margin: 0.5in; 
            size: letter;
          }

          :root {
            --dini-pink: #FF68C5;
            --dini-pink-light: #FF98D9;
            --dini-pink-dark: #E91E63;
            --text-dark: #2c2c2c;
            --text-light: #666;
            --border-light: #e0e0e0;
            --bg-white: #ffffff;
            --bg-light: #fafafa;
          }

          body { 
            margin: 0 !important;
            padding: 0 !important;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
            background: var(--bg-white) !important;
          }

          .no-print { 
            display: none !important; 
          }

          .total-line-modern .tax-note {
            font-size: 0.8em;
            color: var(--text-light);
            font-style: italic;
            margin-left: 10px;
          }

          .receipt-container {
            background: var(--bg-white) !important;
            color: var(--text-dark) !important;
            font-family: 'Inter', sans-serif !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
            line-height: 1.4 !important;
            font-size: 11px !important;
            position: relative !important;
          }

          /* Subtle background pattern */
          .receipt-container::before {
            content: '' !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            background: linear-gradient(45deg, transparent 49%, var(--dini-pink-light) 49%, var(--dini-pink-light) 51%, transparent 51%) !important;
            background-size: 12px 12px !important;
            opacity: 0.03 !important;
            z-index: -1 !important;
          }

          .modern-header {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            margin-bottom: 25px !important;
            padding-bottom: 15px !important;
            border-bottom: 2px solid var(--dini-pink) !important;
            padding: 20px !important;
            margin: -20px -20px 25px -20px !important;
            border-radius: 0 0 12px 12px !important;
          }

          .company-info-left {
            font-family: 'Inter', monospace !important;
            font-size: 9px !important;
            line-height: 1.3 !important;
            color: var(--text-light) !important;
            font-weight: 400 !important;
            max-width: 200px !important;
          }

          .company-info-left strong {
            color: var(--text-dark) !important;
            font-weight: 600 !important;
          }

          .invoice-title-large {
            font-family: 'Playfair Display', serif !important;
            font-size: 38px !important;
            font-weight: 700 !important;
            color: var(--text-dark) !important;
            margin: 0 !important;
            letter-spacing: -1.2px !important;
            line-height: 0.85 !important;
            text-shadow: 1px 1px 0 var(--dini-pink-light) !important;
          }

          /* Side by side layout for invoice details and bill-to */
          .details-billing-container {
            display: flex !important;
            gap: 20px !important;
            margin-bottom: 20px !important;
          }

          .invoice-details {
            flex: 1 !important;
            font-family: 'Inter', monospace !important;
            font-size: 12px !important;
            line-height: 1.4 !important;
            background: var(--bg-light) !important;
            padding: 12px !important;
            border-radius: 6px !important;
            border-left: 3px solid var(--dini-pink) !important;
          }

          .invoice-details div {
            margin-bottom: 2px !important;
            display: flex !important;
            justify-content: space-between !important;
          }

          .invoice-details strong {
            color: var(--text-dark) !important;
            font-weight: 600 !important;
          }

          .bill-to-section {
            flex: 1 !important;
            font-family: 'Inter', monospace !important;
            font-size: 12px !important;
            line-height: 1.3 !important;
            background: var(--bg-white) !important;
            border: 1px solid var(--border-light) !important;
            padding: 12px !important;
            border-radius: 6px !important;
          }

          .bill-to-title {
            font-weight: 700 !important;
            margin-bottom: 8px !important;
            font-size: 10px !important;
            color: var(--text-dark) !important;
            letter-spacing: 0.3px !important;
            text-transform: uppercase !important;
            border-bottom: 1px solid var(--dini-pink-light) !important;
            padding-bottom: 4px !important;
          }

          .bill-to-section div:not(.bill-to-title) {
            margin-bottom: 2px !important;
            color: var(--text-dark) !important;
          }

          .modern-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-bottom: 20px !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            box-shadow: 0 2px 8px rgba(26, 26, 26, 0.1) !important;
          }

          .modern-table th {
            background: linear-gradient(135deg, var(--dini-pink), var(--dini-pink-dark)) !important;
            color: white !important;
            padding: 10px 8px !important;
            font-weight: 600 !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            text-align: left !important;
            letter-spacing: 0.5px !important;
            text-transform: uppercase !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .modern-table th:last-child {
            text-align: right !important;
          }

          .modern-table td {
            padding: 8px !important;
            border-bottom: 1px solid var(--border-light) !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            vertical-align: top !important;
            background: white !important;
          }

          .modern-table td:last-child {
            text-align: right !important;
            font-weight: 600 !important;
            color: var(--text-dark) !important;
          }

          .modern-table td:nth-child(2) {
            text-align: center !important;
            font-weight: 500 !important;
          }

          .modern-table td:nth-child(3) {
            text-align: right !important;
            color: var(--text-light) !important;
          }

          .modern-table tbody tr:nth-child(even) {
            background: var(--bg-light) !important;
          }

          .modern-table tbody tr:hover {
            background: var(--dini-pink-light) !important;
            background-opacity: 0.1 !important;
          }

          .modern-table tbody tr:last-child td {
            border-bottom: 2px solid var(--dini-pink) !important;
          }

          .totals-section-modern {
            margin-top: 15px !important;
            padding: 15px !important;
            background: var(--bg-light) !important;
            border: 1px solid var(--border-light) !important;
            border-radius: 8px !important;
            max-width: 250px !important;
            margin-left: auto !important;
          }

          .total-line-modern {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-bottom: 4px !important;
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            padding: 2px 0 !important;
          }

          .total-line-modern span:first-child {
            color: var(--text-light) !important;
            font-weight: 500 !important;
          }

          .total-line-modern span:last-child {
            font-weight: 600 !important;
            color: var(--text-dark) !important;
          }

          .total-line-modern.final {
            font-weight: 700 !important;
            font-size: 12px !important;
            margin-top: 8px !important;
            padding-top: 8px !important;
            border-top: 2px solid var(--dini-pink) !important;
            color: var(--text-dark) !important;
            background: var(--dini-pink-light) !important;
            background-opacity: 0.1 !important;
            padding: 8px 6px 6px 6px !important;
            border-radius: 4px !important;
          }

          .total-line-modern.final span {
            color: var(--text-dark) !important;
            font-weight: 700 !important;
          }

          .thank-you-footer {
            background: var(--bg-white) !important;
            color: var(--text-dark) !important;
            padding: 15px 20px !important;
            margin-top: 20px !important;
            border-radius: 8px !important;
            text-align: center !important;
            border: 2px solid var(--dini-pink) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            position: relative !important;
            overflow: hidden !important;
          }

          .thank-you-footer::before {
            content: '' !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            height: 4px !important;
            background: linear-gradient(90deg, var(--dini-pink-light), var(--dini-pink), var(--dini-pink-light)) !important;
          }

          .thank-you-text {
            font-family: 'Playfair Display', serif !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            margin-bottom: 8px !important;
            font-style: italic !important;
            letter-spacing: -0.2px !important;
          }

          .footer-contact {
            font-family: 'Inter', monospace !important;
            font-size: 10px !important;
            line-height: 1 !important;
            opacity: 0.6 !important;
            font-weight: 400 !important;
          }
.warranty-section {
  margin: 10px 0 !important; /* Reduced from 20px */
  padding: 8px !important; /* Reduced from 12px */
  background: var(--bg-light) !important;
  border: 1px solid var(--border-light) !important;
  border-radius: 4px !important; /* Reduced from 6px */
  font-family: 'Inter', monospace !important;
  font-size: 7px !important; /* Reduced from 8px */
  line-height: 1.2 !important; /* Tighter line height */
  text-align: center !important;
  position: relative !important;
  page-break-inside: avoid !important; /* Prevent breaking inside */
}

.warranty-section::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  width: 30px !important; /* Reduced from 40px */
  height: 1px !important; /* Reduced from 2px */
  background: var(--dini-pink) !important;
}

.warranty-title {
  font-family: 'Playfair Display', serif !important;
  font-weight: 600 !important;
  margin-bottom: 4px !important; /* Reduced from 6px */
  font-size: 12px !important; /* Reduced from 10px */
  color: var(--text-dark) !important;
  font-style: italic !important;
  margin-top: 2px !important; /* Reduced from 4px */
}

.warranty-text {
  color: var(--text-dark) !important;
  font-style: italic !important;
  font-size: 10px !important;
  max-width: 800px !important; /* Reduced from 350px */
  margin: 0 auto !important;
  line-height: 1.1 !important; /* Tighter text */
}

          /* Decorative elements */
          .section-divider {
            height: 1px !important;
            background: linear-gradient(90deg, transparent, var(--dini-pink-light), transparent) !important;
            margin: 12px 0 !important;
          }

          /* Enhanced typography hierarchy */
          h1, h2, h3 {
            font-family: 'Playfair Display', serif !important;
            color: var(--text-dark) !important;
          }

          .accent-line {
            width: 30px !important;
            height: 2px !important;
            background: var(--dini-pink) !important;
            margin: 6px 0 !important;
          }
        </style>
      `;
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice - Order #${orderDetails?.orderNo || 'N/A'}</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
        </html>
      `;
      
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        try {
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => {
              if (printWindow && !printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 1000);
        } catch (printError) {
          console.error('Error during printing:', printError);
          alert('Error occurred while preparing print. Please try again.');
        }
      }, 100);
      
    } catch (error) {
      console.error('Error in print function:', error);
      alert('Error occurred while preparing print. Please try again.');
    }
  }, [orderDetails?.orderNo]);

  if (!isVisible) return null;



  return (
    <div 
      className="modal show d-block" 
      style={{ 
        backgroundColor: 'rgba(0,0,0,0.8)', 
        zIndex: 1050,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'auto'
      }}
      onClick={onClose}  
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header no-print">
            <h5 className="modal-title">
              <i className="fas fa-receipt mr-2"></i>
              Order Receipt - #{orderDetails?.orderNo}
            </h5>
           <div>
  <button 
    className="btn btn-primary mr-2" 
    onClick={handlePrint}
  >
    <i className="fas fa-print mr-1"></i>
    Print Again
  </button>

<EmailInvoice 
  orderDetails={orderDetails}
  customerInfo={customerInfo}
  orderItems={orderItems}
  payments={payments}
  lineItemsData={lineItemsData}
  paymentsData={paymentsData}
  buttonText="📧 Email Real"
  buttonClassName="btn btn-warning mr-2"
  onSuccess={(result) => {
    console.log('✅ Email sent:', result);
    alert(`Invoice emailed to ${result.customerEmail}!`);
  }}
  onError={(error) => {
    console.error('❌ Email failed:', error);
    alert(`Email failed: ${error.message}`);
  }}
/>
  
  <button 
    type="button" 
    className="btn btn-secondary" 
    onClick={onClose}
  >
    <i className="fas fa-times mr-1"></i>
    Close
  </button>
</div>
          </div>
          
          <div className="modal-body p-4">
            <div ref={receiptRef} className="receipt-container">
              {/* Modern Header */}
              <div className="modern-header">
                <div className="company-info-left">
                  <div>{companyInfo.address}, {companyInfo.cityState}</div>
                  <div>{companyInfo.phone}</div>
                  <div>{companyInfo.website}</div>
                  
                  <div className="invoice-title-large">INVOICE</div>
                </div>
                <div className="company-logo-right">
                  <img 
                    src="/images/dini_title.png" 
                    alt="Company Logo"
                    style={{
                      width: '150px',
                      height: '100px',
                    }}
                  />
                </div>
              </div>

             <div className="details-billing-container">
              {/* Invoice Details */}
              <div className="invoice-details">
                <div><strong>Issued:</strong> {formatDate(orderDetails?.orderDate)}</div>
                <div><strong>Order Number:</strong> {orderDetails?.orderNo}</div>
                <div><strong>Location:</strong> {orderDetails?.warehouse || orderDetails?.location || orderDetails?.orderWarehouse || 'Pomona'}</div>
                <div><strong>Order Type:</strong> {orderDetails?.orderType}</div>
                <div><strong>Due Date:</strong> {formatDate(orderDetails?.dueDate)}</div>
                <div><strong>Wig Code:</strong> {orderDetails?.wigBarCode}</div>
              </div>

{/* Bill To Section */}
<div className="bill-to-section">
  <div className="bill-to-title">BILL TO:</div>
  
  {loading ? (
    <div>Loading customer information...</div>
  ) : error ? (
    <div>Error loading customer: {error}</div>
  ) : (
    <>
      <div>
        {customerInfo?.CustomerFullName || 
         `${customerInfo?.CustomerFirstName || ''} ${customerInfo?.CustomerLastName || ''}`.trim() ||
         orderDetails?.OrderCustomerName || 
         'Customer name not available'}
      </div>
      
      <div>
        {customerInfo?.CustomerPhone || 
         orderDetails?.OrderPhone || 
         'Phone not available'}
      </div>
      
      <div>
        {customerInfo?.CustomerAddress || 
         orderDetails?.OrderCustomerAddress || 
         'Address not available'}
      </div>
      
      <div>
        {[
          customerInfo?.CustomerCity || orderDetails?.OrderCity,
          customerInfo?.CustomerState || orderDetails?.OrderSt || orderDetails?.OrderState,
          customerInfo?.CustomerZipCode || orderDetails?.OrderZipCode || orderDetails?.OrderZip
        ].filter(Boolean).join(', ') || 'City, State, Zip not available'}
      </div>
      
      {customerInfo?.CustomerEmail && (
        <div>{customerInfo.CustomerEmail}</div>
      )}
    </>
  )}
</div>

{/* SHIP TO SECTION */}
<div className="bill-to-section" style={{ marginTop: 8 }}>
  <div className="bill-to-title">SHIP TO:</div>
  {isLoadingShipping ? (
    <div>Loading shipping address...</div>
  ) : shippingInfo ? (
    <>
      <div>
        {customerInfo?.CustomerFullName || 
         `${customerInfo?.CustomerFirstName || ''} ${customerInfo?.CustomerLastName || ''}`.trim() ||
         orderDetails?.OrderCustomerName}
      </div>
      <div>{customerInfo?.CustomerPhone || orderDetails?.OrderPhone}</div>
      <div style={{ whiteSpace: 'pre-line' }}>{shippingInfo}</div>
    </>
  ) : (
    // Fallback to billing address when no shipping address
    <>
      <div>
        {customerInfo?.CustomerFullName || 
         `${customerInfo?.CustomerFirstName || ''} ${customerInfo?.CustomerLastName || ''}`.trim() ||
         orderDetails?.OrderCustomerName}
      </div>
      <div>{customerInfo?.CustomerPhone || orderDetails?.OrderPhone}</div>
      <div>{customerInfo?.CustomerAddress || orderDetails?.OrderCustomerAddress || 'Address not available'}</div>
      <div>
        {[
          customerInfo?.CustomerCity || orderDetails?.OrderCity,
          customerInfo?.CustomerState || orderDetails?.OrderSt || orderDetails?.OrderState, 
          customerInfo?.CustomerZipCode || orderDetails?.OrderZipCode || orderDetails?.OrderZip
        ].filter(Boolean).join(', ') || 'City, State, Zip not available'}
      </div>
    </>
  )}
</div>

            </div>

              {/* Items Table */}
              <table className="modern-table">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>DESCRIPTION</th>
                    <th style={{ width: '15%' }}>PAYMENT METHOD</th>
                    <th style={{ width: '10%' }}>DATE</th>
                    <th style={{ width: '10%' }}>QTY</th>
                    <th style={{ width: '20%' }}>PRICE</th>
                    <th style={{ width: '15%' }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Line Items */}
                  {(() => {
                    const itemsToDisplay = lineItemsData?.items || orderItems || [];
                    return itemsToDisplay
                     
                      .map((item, index) => (
                        <tr key={`item-${index}`}>
                          <td>{item.OrderItemDescription || item.description}</td>
                          <td></td>
                          <td>{formatDate(item.OrderItemDate || item.date)}</td>
                          <td>{item.OrderQty || item.quantity || 1}</td>
                          <td>{formatCurrency(item.OrderItemRetailCost || item.price || item.retailCost)}</td>
                          <td>{formatCurrency(item.OrderItemExtendedCost || item.extendedCost || ((item.price || 0) * (item.quantity || 1)))}</td>
                        </tr>
                      ));
                  })()}
                  
                  
{/* Payment Records */}
{(() => {
  const paymentsToDisplay = paymentsData?.payments || payments || [];
  const paymentRows = [];
  
  paymentsToDisplay.forEach((payment, index) => {
    const paymentType = (payment.PaymentType || '').toLowerCase();
    const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);
    const paymentMethod = payment.PaymentMethod || payment.method || 'Unknown';
    const paymentDate = payment.PaymentDate || payment.date;
    
    // Main payment row (exclude tax-only payments from main display)
    if (!paymentType.includes('tax')) {
      paymentRows.push(
        <tr key={`payment-${index}`}>
          <td>{payment.PaymentType || `Payment - ${paymentMethod}`}</td>
          <td>{paymentMethod}</td>
          <td>{formatDate(paymentDate)}</td>
          <td>1</td>
          <td>({formatCurrency(paymentAmount)})</td>
          <td>({formatCurrency(paymentAmount)})</td>
        </tr>
      );
    } else {
      // Tax payment row
      paymentRows.push(
        <tr key={`tax-payment-${index}`}>
          <td>Tax Payment - {paymentMethod}</td>
          <td>{paymentMethod}</td>
          <td>{formatDate(paymentDate)}</td>
          <td>1</td>
          <td>({formatCurrency(paymentAmount)})</td>
          <td>({formatCurrency(paymentAmount)})</td>
        </tr>
      );
    }
  });
  
  return paymentRows;
})()}
                </tbody>
              </table>

              {/* Enhanced Totals Section */}
                <div className="totals-section-modern">
                  {(() => {
                    // Calculate all the financial details
                    const itemsToUse = lineItemsData?.items || orderItems || [];
                    const paymentsToUse = paymentsData?.payments || payments || [];
                    
                    // Calculate purchase subtotal (excluding tax items)
                    const purchaseSubtotal = itemsToUse.reduce((sum, item) => {
                      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
                      if (description.includes('tax') || description.includes('processing fee') || description.includes('credit card fee')) {
                        return sum;
                      }
                      return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
                    }, 0);
                    
                    // Calculate total tax from line items
                    const totalTax = itemsToUse.reduce((sum, item) => {
                      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
                      if (description.includes('tax applied') || (description.includes('tax') && !description.includes('credit card'))) {
                        return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
                      }
                      return sum;
                    }, 0);
                    
                    // Calculate tax paid to date
                    const taxPaidToDate = paymentsToUse.reduce((sum, payment) => {
                      const paymentType = (payment.PaymentType || '').toLowerCase();
                      if (paymentType.includes('tax')) {
                        return sum + parseFloat(payment.PaymentAmount || payment.amount || 0);
                      }
                      return sum;
                    }, 0);
                    
                    // Calculate credit card fees
                    const creditCardFees = paymentsToUse.reduce((sum, payment) => {
                      const ccFee = parseFloat(payment.CreditCardFee || payment.cardFee || payment.creditCardFee || 0);
                      return sum + ccFee;
                    }, 0);
                    
                    // Calculate total payments (excluding tax payments)
                    const totalPayments = paymentsToUse.reduce((sum, payment) => {
                      const paymentType = (payment.PaymentType || '').toLowerCase();
                      if (!paymentType.includes('tax')) {
                        return sum + parseFloat(payment.PaymentAmount || payment.amount || 0);
                      }
                      return sum;
                    }, 0);
                    
                    // Calculate total amount due (subtotal + tax - total payments - tax paid)
                    const totalAmountDue = purchaseSubtotal + totalTax - totalPayments - taxPaidToDate;
                    
                    // Get current date for display
                    const currentDate = new Date().toLocaleDateString('en-US', { 
                      month: '2-digit', 
                      day: '2-digit', 
                      year: 'numeric' 
                    });
                    
                    return (
                      <>
                        <div className="total-line-modern">
                          <span>Purchase Subtotal:</span>
                          <span>{formatCurrency(purchaseSubtotal)}</span>
                        </div>
                        <div className="total-line-modern">
                          <span style={{ fontSize: '0.8em', color: 'var(--text-light)', fontStyle: 'italic' }}>
                            (tax not included)
                          </span>
                          <span></span>
                        </div>
                        
                        {totalTax > 0 && (
                          <div className="total-line-modern">
                            <span>Tax:</span>
                            <span>{formatCurrency(totalTax)}</span>
                          </div>
                        )}
                        
                        {taxPaidToDate > 0 && (
                          <div className="total-line-modern">
                            <span>Tax Paid to Date:</span>
                            <span>{formatCurrency(taxPaidToDate)}</span>
                          </div>
                        )}
                        
                        {creditCardFees > 0 && (
                          <div className="total-line-modern">
                            <span>Credit Card Fees:</span>
                            <span>{formatCurrency(creditCardFees)}</span>
                          </div>
                        )}
                        
                        <div className="total-line-modern">
                          <span>Total Payments to Date: ({currentDate})</span>
                          <span>({formatCurrency(totalPayments)})</span>
                        </div>
                        
                        <div className="total-line-modern final">
                          <span>TOTAL AMOUNT DUE:</span>
                          <span>{formatCurrency(Math.max(0, totalAmountDue))}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

              {/* Thank You Footer
              <div className="thank-you-footer">
                <div className="thank-you-text">Thank you!</div>
                <div className="footer-contact">
                  {companyInfo.address}, {companyInfo.cityState}<br/>
                  {companyInfo.phone}<br/>
                  {companyInfo.website}
                </div>
              </div> */}

              {/* Warranty Information - Show for Wig Repair, New Wig, or New Sale Wig orders */}
              {['Wig Repair', 'New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderDetails?.orderType)) && (
                <div className="warranty-section">
                  <div className="warranty-title">Important Policy Information</div>
                  <div className="warranty-text">
                    Please be aware of the following policies: Prices are subject to change as we inspect the repair. 
                    Please be advised that a Dini Wigs representative may call you before we begin alterations in case 
                    of pricing discrepancies. All paid alterations include wash and set in the cost. All alterations 
                    covered by the warranty are subject to either wash and set or touch-up charge. All Prices are 
                    subject to applicable taxes. A 3% processing fee is applied to credit card purchases 
                    exceeding $150. If you would like to request for your wig not to be washed please let our staff know. 
                    For more information please check our policy on our website at www.diniwigs.com/terms-and-conditions-of-purchase
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderReceipt;